/* eslint-disable @typescript-eslint/member-ordering,arrow-body-style */
import {action, computed, makeObservable, observable, reaction, runInAction} from "mobx";

import * as d3 from "d3";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const _ = require("lodash");

import {Station, StationProps} from "./station";
import {TimeSeries} from "./time-series";
import {DateRange, DateRangeQueryParams} from "./date-range";
import {Measurement} from "./measurement";

import {KiwisConfiguration} from "../config/kiwis-configuration";

import esriRequest from "@arcgis/core/request";
import {DataAggregationType} from "../config/generalisation-configuration";
import {GeneralisationRuleConfiguration, PluckIndex} from "../config/generalisation-rule-configuration";

import {GroupConfiguration} from "../config/group-configuration";
import {DataValueType} from "./data-value-type";
import {StationDocuments} from "./station-documents";
import {PathUtils} from "../utils/path-utils";
import {SelectOptionConfiguration} from "../core/config/select-option-configuration";
import dayjs = require("dayjs");

import { configure } from "mobx";
import {
    StationListQueryConfiguration,
    TimeSeriesListQueryConfiguration,
    TimeSeriesValuesQueryConfiguration
} from "../config/kiwis-query-configuration";

configure({
    enforceActions: "always",
    computedRequiresReaction: true,
    reactionRequiresObservable: true,
    observableRequiresReaction: false,  // was true
    disableErrorBoundaries: false  // was true
});

interface TimeSeriesPeriodTuple {
    id: string;
    periodISO: string;
    from: string;
    to: string;
}

// eslint-disable-next-line no-shadow
export enum StatusType {
    Loading = "active",
    Slowly = "warning",
    Complete = "successful",
    Error = "error"
}

// eslint-disable-next-line no-shadow
export enum DataCompleteness {
    // noinspection JSUnusedGlobalSymbols
    Empty = "empty",
    Stations = "stations",
    TimeSeriesLists = "timeSeriesLists",
    TimeSeriesData = "timeSeriesData",
    CurrentMeasurements = "currentMeasurements",
    MeasurementCode = "measurementCode"
}

// eslint-disable-next-line no-shadow
export enum DocumentsCompleteness {
    Empty = "empty",
    Complete = "complete"
}

// eslint-disable-next-line no-shadow
export enum GeneralisationStatus {
    Calculating = "Generalisierung",
    Complete = "Generalisierung abgeschlossen",
    Error = "Fehler bei Generalisierung"
}

export class Kiwis {
    /**
     * Extract coordinate from string
     *
     * @param coordinate: Coordinate string input
     * @param origin: The minimum value for a valid coordinate
     */
    private static extractCoordinate(coordinate: string, origin: number) {
        if (coordinate === "") {
            // TODO log error
            return -1;
        }

        const c = Number(coordinate);
        if (c < origin) {
            return c + origin;
        }
        return c;
    }

    config: KiwisConfiguration;

    /**
     * Status representing current interactions with KIWIS
     */
    @observable status = observable.array([{message: "Karte wird geladen...", type: StatusType.Loading}]);

    @observable dataCompleteness = DataCompleteness.Empty;
    @observable documentsCompleteness = DocumentsCompleteness.Empty;
    @observable generalisationStatus = GeneralisationStatus.Complete;
    @observable dataValuesType = DataValueType.Absolute;

    // loaded/availbale stations
    /**
     * OID is used as key
     */
    @observable stations = observable.map<number, Station>();

    // remember the selected station
    @observable selectedStationId = observable.box(1);

    // remember the selected time series
    @observable selectedTimeSeriesId = observable.box("");

    // list with loaded time series
    @observable timeSeriesList = observable.map<string, TimeSeries>();

    /**
     * List to remember visible time series.
     * Only data of visible time series should be loaded from KIWIS
     */
    @observable visibleTimeSeries = observable.map<string, TimeSeries>();

    /**
     * list with loaded time series data (data of a parameter)
     * key: time series id
     * Only values of specified period are stored.
     * Data is replaced when period is changed for this time series
     */
    @observable timeSeriesData = observable.map<string, TimeSeriesDataTuple[] | null>();

    /**
     * Time when data of a time series was loaded the last time.
     * Timestamp for each loaded time series.
     */
    @observable timeSeriesDataTimestamps = observable.map<string, Date | null>();

    /**
     * Available thresholds per time series
     */
    @observable availableThresholds = observable.map<string, SelectOptionConfiguration[] | null>();
    /**
     * Selected thresholds per time series
     */
    @observable selectedThresholds = observable.map<string, string[] | null>();
    /**
     * Values of selected Thresholds
     */
    @observable selectedThresholdValues = observable.map<string, number[]>();
    /**
     * Min value of the selected thresholds per time series
     */
    @observable selectedMinThresholdValue = observable.map<string, number>();
    /**
     * Max value of the selected thresholds per time series
     */
    @observable selectedMaxThresholdValue = observable.map<string, number>();

    /**
     * Documents from MONITRON documents server for each station.
     * Station NO is used as key
     */
    @observable stationDocuments = observable.map<string, StationDocuments>();

    /**
     * Reaction after all stations have been loaded from kiwis.
     * (after stations were stored)
     */
    disposeStationsLoadedReaction = reaction(
        () => [...this.stations.entries()].map((entry) => entry[1]),
        (stations) => {
            if (stations) {
                this.identifyMultiObjectTypeStations(stations);
                this.loadAllTimeSeriesLists(stations);
                this.getAllStationDocumentUrls();
                this.loadLongTermThresholds(stations);
            }
        }
    );

    /**
     * Reaction after visible time series were changed.
     * (Used to load time series data)
     */
    disposeVisibleTimeSeriesListReaction = reaction(
        () => [...this.visibleTimeSeries.values()],
        (values) => values.forEach((ts) =>
            // load time series values, values are displayed as soon as they are available
            this.loadTimeSeriesData(ts.id, false)
        )
    );

    /**
     * Reaction after time series were loaded/changed.
     * (Used to load current measurement values for each time series)
     */
    disposeTimeSeriesListReaction = reaction(
        () => [...this.timeSeriesList.keys()],
        (tsIds) => (
            // load time series values, values are displayed as soon as they are available
            this.loadCurrentMeasurements(tsIds)
        )
    );

    // noinspection JSUnusedLocalSymbols
    /**
     * Reaction after period has changed for a time series.
     * (Used to reload time series data)
     */
    disposeTimeSeriesPeriodReaction = reaction(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        () => [...this.timeSeriesList].map(([key, value]) =>
            // call to trigger reaction on period changes
            ({id: value.id, periodISO: value.periodISO, from: value.from, to: value.to})
        ),
        (values) => values.forEach((tst: TimeSeriesPeriodTuple) => {
            if (!tst.periodISO && !tst.from && !tst.to) {
                return;
            }
            if (this.visibleTimeSeries.has(tst.id)) {
                // load time series values, values are displayed as soon as they are available
                this.loadTimeSeriesData(tst.id, true);
            }
        })
    );

    /**
     * Reaction after selected thresholds were changed.
     * (Used to calc min / max threshold values)
     */
    disposeThresholdSelectedReaction = reaction(
        () => [...this.selectedThresholds.entries()],
        (thresholds) => {
            thresholds.forEach(([key, values]) => {
                this.storeThresholdValues(key, values);
            });
        }
    );

    constructor(config: KiwisConfiguration) {
        makeObservable(this);
        this.config = config;
    }

    @action
    addStation(station: Station) {
        if (this.stations.has(station.oid)) {
            this.stations.delete(station.oid);
        }
        this.stations.set(station.oid, station);
    }

    @action
    addStations(stations: Map<number, Station>) {
        this.stations.merge(stations);
    }

    @action
    addDocuments(documents: Map<string, StationDocuments>) {
        this.stationDocuments.merge(documents);
    }

    @action
    addTimeSeries(timeSeries: TimeSeries) {
        if (this.timeSeriesList.has(timeSeries.id)) {
            this.timeSeriesList.delete(timeSeries.id);
        }
        this.timeSeriesList.set(timeSeries.id, timeSeries);
    }

    @action
    addTimeSeriesList(timeSeriesList: Map<string, TimeSeries>) {
        this.timeSeriesList.merge(timeSeriesList);
    }

    @action
    replaceTimeSeriesList(timeSeriesList: Map<string, TimeSeries>) {
        this.timeSeriesList.replace(timeSeriesList);
    }

    @action
    addTimeSeriesData(timeSeriesId: string, data: TimeSeriesDataTuple[] | null) {
        if (this.timeSeriesData.has(timeSeriesId)) {
            this.timeSeriesData.delete(timeSeriesId);
        }
        this.timeSeriesData.set(timeSeriesId, data);
    }

    @action
    clearTimeSeriesData(timeSeriesId: string) {
        this.addTimeSeriesData(timeSeriesId, null);
    }

    selectedStation(): Station | null {
        const stationOid = this.selectedStationId.get();
        if (!stationOid) {
            return null;
        }
        const station = this.stations.get(stationOid);
        return station || null;
    }

    /**
     * Remember current selected station
     */
    @action
    selectStation(stationOid: number) {
        this.selectedStationId.set(stationOid);
    }

    @action
    updateDataCompleteness(status: DataCompleteness) {
        this.dataCompleteness = status;
    }

    @computed
    get currentMeasurementsLoaded(): boolean {
        return this.dataCompleteness === DataCompleteness.CurrentMeasurements;
    }

    @action
    updateDocumentCompleteness(status: DocumentsCompleteness) {
        this.documentsCompleteness = status;
    }

    @action
    updateGeneralisationStatus(status: GeneralisationStatus) {
        this.generalisationStatus = status;
    }

    /**
     * Set KIWIS state
     */
    @action
    setStatus(message: string, type: StatusType) {
        this.status[this.status.length] = {message, type};
    }

    /**
     * Set available thresholds
     */
    @action
    setAvailableThresholds(timeSeriesId: string, thresholds: SelectOptionConfiguration[]) {
        this.availableThresholds.set(timeSeriesId, thresholds);
    }

    /**
     * Set selected thresholds
     */
    @action
    setSelectedThresholds(timeSeriesId: string, thresholds: string[]) {
        this.selectedThresholds.set(timeSeriesId, thresholds);
    }

    /**
     * Set time stamp of a time series
     */
    @action
    setTimeSeriesDataTimestamp(timeSeriesId: string, timeStamp: Date) {
        this.timeSeriesDataTimestamps.set(timeSeriesId, timeStamp);
    }

    /**
     * Get station that belongs to time series
     *
     * @param ts
     */
    getStationByTimeSeries(ts: TimeSeries) {
        const parameterType = ts.parameterTypeName;
        const baseIds = this.config.stations.parameterTypeObjectTypeIdMappings[parameterType];
        if (!baseIds || baseIds.length === 0) {
            // eslint-disable-next-line no-console
            console.warn(`ParameterType <-> ObjectType mapping not specified in configuration
                                  for: ${parameterType}`);
            return null;
        }

        const stations = baseIds.map((baseId: string) => {
            const oid = Number(baseId + ts.stationId);
            const station = this.stations.get(oid);
            if (station) {
                return station;
            }
        }).filter((s: any) => s != null);
        if (!stations.length) {
            return null;
        }
        return stations[0];
    }

    /**
     * Return currently selected time series
     */
    @computed
    get selectedTimeSeries(): TimeSeries | null {
        const tsId = this.selectedTimeSeriesId.get();
        if (!tsId) {
            return null;
        }
        const ts = this.timeSeriesList.get(tsId);
        return ts || null;
    }

    /**
     * Remember the selected time series
     */
    @action
    selectTimeSeries(tsId: string) {
        this.selectedTimeSeriesId.set(tsId);
    }

    /**
     * Update visible time series
     */
    @action
    updateVisibleTimeSeries(previousTimeSeries: TimeSeries | undefined, newTimeSeries: TimeSeries | undefined) {
        if (previousTimeSeries) {
            this.visibleTimeSeries.delete(previousTimeSeries.id);
        }
        if (newTimeSeries) {
            this.visibleTimeSeries.set(newTimeSeries.id, newTimeSeries);
        }
    }

    /**
     * Store selected period for a time series
     */
    @action
    selectPeriod(timeSeriesId: string, period: string | DateRange) {
        if (!this.timeSeriesList.has(timeSeriesId)) {
            return;
        }
        const ts = this.timeSeriesList.get(timeSeriesId);
        if (!ts) {
            return;
        }
        if (typeof period === "string") {
            ts.periodISO = period;
        }
        if (period instanceof DateRange) {
            ts.from = period.from;
            ts.to = period.to;
        }
    }

    /**
     * Load available stations
     * (Stations are filtered by group. Group is specifiec in query in configuration file)
     */
    @action
    loadStations() {
        const query = this.getQuery("allStations") as StationListQueryConfiguration;
        if (!query) {
            throw new Error("query for 'allStations' not defined in 'queries'.");
        }
        this.log("> Stationen von KIWIS abfragen...", StatusType.Loading);

        // eslint-disable-next-line no-console
        console.time("KIWIS: Load stations");
        let requestTimeoutTimer: NodeJS.Timeout;
        const loadingTimer = setTimeout(() => {
            // report when kiwis is slow loading stations
            this.log("KIWIS reagiert langsam...", StatusType.Slowly);
            requestTimeoutTimer = setTimeout(() => {
                this.log("KIWIS zur Zeit nicht verfübar...", StatusType.Error);
            }, 60000);
        }, 3000);

        // reset state
        this.updateDataCompleteness(DataCompleteness.Empty);

        // enable post request. get request with lots of time series IDs fails
        query.kvp = true;

        esriRequest(this.config.url, {
            responseType: "json",
            method: "post",
            query
        }).then((result) => {
            // eslint-disable-next-line no-console
            console.timeEnd("KIWIS: Load stations");
            clearTimeout(loadingTimer);
            clearTimeout(requestTimeoutTimer);
            this.log("> Stationsdaten erhalten.", StatusType.Loading);

            const stations = this.extractStations(result);
            runInAction(() => {
                this.addStations(stations);
            });

            this.log("> Stationen geladen.", StatusType.Complete);
            this.updateDataCompleteness(DataCompleteness.Stations);
        }, (error) => {
            clearTimeout(loadingTimer);
            const msg = `Fehler beim Laden der Stationen: ${error}`;
            this.log("Fehler beim Laden der Stationen.", StatusType.Error);
            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
        });
    }

    /**
     * Load station document URLs (images, PDF) from external document server
     */
    @action
    getAllStationDocumentUrls() {
        const url = PathUtils.joinPath(this.config.stationDocumentsBaseUrl, this.config.stationDocumentsServiceUrl);
        if (!url) {
            throw new Error("URL for station documents not defined.");
        }

        // reset state
        this.updateDocumentCompleteness(DocumentsCompleteness.Empty);
        this.log("Dokumente von Dokumentenserver abfragen...", StatusType.Loading);

        // eslint-disable-next-line no-console
        console.time("Load documents");
        let requestTimeoutTimer: NodeJS.Timeout;
        const loadingTimer = setTimeout(() => {
            // report when document server is slow loading documents
            this.log("Dokumentenserver reagiert langsam...", StatusType.Slowly);
            requestTimeoutTimer = setTimeout(() => {
                this.log("Dokumentenserver zur Zeit nicht verfübar", StatusType.Error);
            }, 60000);
        }, 3000);

        esriRequest(url, {
            responseType: "json"
        }).then((result: any) => {
            clearTimeout(loadingTimer);
            clearTimeout(requestTimeoutTimer);
            this.log("Dokumente erhalten.", StatusType.Loading);

            const documents = this.parseDocumentsResult(result.data);
            this.addDocuments(documents);

            this.updateDocumentCompleteness(DocumentsCompleteness.Complete);
            this.log("Dokumente geladen.", StatusType.Complete);
        }).catch((error: any) => {
            // eslint-disable-next-line no-console
            console.error("Fehler beim Laden der Stationsdokumente: " + error);
        });
    }

    /**
     * Load available time series of a station
     *
     * @param stationId: Id of the station.
     */
    @action
    loadTimeSeriesList(stationId: number) {
        const query = this.getQuery("timeSeriesList") as TimeSeriesListQueryConfiguration;
        if (!query) {
            throw new Error("query for 'timeSeriesList' not defined in 'queries'.");
        }

        this.log(">> Verfügbare Messparameter laden...", StatusType.Loading);
        const loadingTimer = setTimeout(() => {
            // report when KIWIS is loading slowly
            this.log("KIWIS reagiert langsam...", StatusType.Slowly);
        }, 4000);

        // enable post request. get request with lots of time series IDs fails
        query.kvp = true;

        query.station_id = String(stationId);
        esriRequest(this.config.url, {
            responseType: "json",
            method: "post",
            query
        }).then((result) => {
            clearTimeout(loadingTimer);
            result.data.forEach((item: any) => {
                this.log(">> Messparameter erhalten.", StatusType.Loading);
                this.createOrUpdateTimeSeries(item);
            });
            this.log(">> Messparameter geladen.", StatusType.Complete);
        }, (error) => {
            clearTimeout(loadingTimer);
            const msg = `Fehler beim Laden der Zeitreihenliste: ${error}`;
            this.log("Fehler beim Laden der Zeitreihenliste.", StatusType.Error);
            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
        });
    }

    /**
     * Load data for a time series
     *
     * @param timeSeriesId: ID of the time series
     * @param force: Force reloading
     */
    @action
    loadTimeSeriesData(timeSeriesId: string, force: boolean) {
        const timeSeries = this.timeSeriesList.get(timeSeriesId);
        if (!timeSeries) {
            throw new Error("No time series found with id: " + timeSeriesId);
        }

        const data = this.timeSeriesData.get(timeSeriesId);
        if (data && !force) {
            // only load data the first time or reload when forced by changed period
            return;
        }

        this.log(`Zeitreihe '${timeSeries.name}' laden...`, StatusType.Loading);

        let query = this.getQuery("timeSeriesValues") as TimeSeriesValuesQueryConfiguration;
        if (!query) {
            throw new Error("query for 'timeSeriesValues' not defined in 'queries'.");
        }
        query.ts_id = timeSeries.id;
        // set period
        // TODO: stop on very long time periods to prevent kiwis blocking the whole app
        const dateRange = timeSeries.selectedDateRange;
        if (!dateRange || (!dateRange.to && !dateRange.from)) {
            // clear data, no data available (for this period or at all)
            this.clearTimeSeriesData(timeSeries.id);
            return false;
        }
        const dateRangeParams = dateRange.kiwisQueryParams;
        // extend date range if needed
        const extendedDateRangeParams = this.extendDateRange(timeSeries, dateRangeParams);

        query = Object.assign(query, extendedDateRangeParams);

        const loadingTimer = setTimeout(() => {
            // report when KIWIS is loading slowly
            this.log("KIWIS reagiert langsam...", StatusType.Slowly);
        }, 5000);

        // enable post request. get request with lots of time series IDs fails
        query.kvp = true;

        esriRequest(this.config.url, {
            responseType: "json",
            method: "post",
            query
        }).then((result) => {

            clearTimeout(loadingTimer);
            this.log(`Zeitreihe '${timeSeries.name}' erhalten...`, StatusType.Loading);

            // there should only be one result
            if (!result.data[0].ts_id || !result.data[0].data) {
                this.log(`Zeitreihe '${timeSeries.name}': Keine ID oder keine Daten enthalten`, StatusType.Error);
            }

            const tsId = result.data[0].ts_id;

            // extract first data point to identify relative value position
            const {absoluteValueIndex, relativeValueIndex} = this.extractValues(result.data[0]);
            timeSeries.setDataAbsoluteValueIndex(absoluteValueIndex);
            timeSeries.setDataRelativeValueIndex(relativeValueIndex);

            const tsData = this.generaliseTimeSeriesData(result.data[0].data, tsId, result.data[0].rows);
            this.addTimeSeriesData(tsId, tsData);
            this.setTimeSeriesDataTimestamp(tsId, new Date());
            this.log(`Zeitreihe '${timeSeries.name}' geladen...`, StatusType.Complete);

        }, (error) => {
            clearTimeout(loadingTimer);
            const msg = `Fehler beim Laden der Zeitreihendaten: ${error}`;
            this.log("Fehler beim Laden der Zeitreihendaten.", StatusType.Error);
            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
        });
    }

    /**
     * Update time series data
     */
    @action
    reloadTimeSeriesData() {
        const ts = this.selectedTimeSeries;
        if (!ts) {
            return;
        }
        if (this.visibleTimeSeries.has(ts.id)) {
            // reload time series data, because original relative data gets lost on aggregation
            // aggregation is done after loading the data
            this.loadTimeSeriesData(ts.id, true);
        }
    }

    /**
     * Load Min, Mean, Max thresholds from timeseries holding long term threshold values.
     * All these time series are hold in a WISKI group for easy access
     *
     * Query all time series of long term thresholds in the specified group.
     * Rrequest without date to get the last value in the time series.
     * The last value represents the most current long term value.
     * Values are calculated in moving window procedure with last values of a period as threshold value of the period
     *
     * @param stations The stations to load thresholds for
     */
    @action
    loadLongTermThresholds(stations: Station[]): void {
        this.log("Langzeit Mittelwerte laden...", StatusType.Loading);

        const query = this.getQuery("longTermThresholds") as TimeSeriesValuesQueryConfiguration;
        if (!query) {
            throw new Error("query for 'longTermThresholds' not defined in 'queries'.");
        }

        const loadingTimer = setTimeout(() => {
            // report when KIWIS is loading slowly
            this.log("KIWIS reagiert langsam...", StatusType.Slowly);
        }, 3000);

        // enable post request. get request with lots of time series IDs fails
        query.kvp = true;

        esriRequest(this.config.url, {
            responseType: "json",
            method: "post",
            query
        }).then((result: __esri.RequestResponse) => {

            clearTimeout(loadingTimer);
            this.log("Langzeit Mittelwerte erhalten...", StatusType.Loading);

            // there should only be one result
            result.data.forEach((ts: any) => {
                const station = stations.find((s: Station) => s.id === ts.station_id);
                if (!station) {
                    return;
                }
                const data = ts.data[0]; // get the only data point from the series
                const shortName = ts.ts_shortname;

                if (shortName.toLowerCase().includes("10-year")) {
                    // set dates of 10-year period
                    // use date of current measurement in time series as max date
                    const maxDate: Date = new Date(data[0]);
                    const minDate: Date = new Date(maxDate);
                    // subtract 10 years from max date
                    minDate.setFullYear(minDate.getFullYear() - 10);
                    const minDateString = dayjs(minDate).format("YYYY-MM-DDTHH:mm:ss");

                    // set values
                    if (shortName === "10-Year.Min") {
                        station.longTermValMin = data[1];
                        station.longTermValMinEndDate = data[0];
                        station.longTermValMinStartDate = minDateString;
                    } else if (shortName === "10-Year.Mean") {
                        station.longTermValMean = data[1];
                        station.longTermValMeanEndDate = data[0];
                        station.longTermValMeanStartDate = minDateString;
                    } else if (shortName === "10-Year.Max") {
                        station.longTermValMax = data[1];
                        station.longTermValMaxEndDate = data[0];
                        station.longTermValMaxStartDate = minDateString;
                    }
                }
            });

            this.log("Langzeit Mittelwerte geladen...", StatusType.Complete);

        }, (error) => {
            clearTimeout(loadingTimer);
            const msg = `Fehler beim Laden der Langzeit Mittelwerte: ${error} ${error?.details?.raw?.message}`;
            this.log("Fehler beim Laden der Langzeit Mittelwerte.", StatusType.Error);
            // eslint-disable-next-line no-console
            console.error(msg, error);
            throw new Error(msg);
        });
    }

    /**
     * Toggle data value type to get relative or absolute data
     */
    @action
    toggleDataValueType() {
        if (this.dataValuesType === DataValueType.Absolute) {
            this.dataValuesType = DataValueType.Relative;
        } else {
            this.dataValuesType = DataValueType.Absolute;
        }
        this.reloadTimeSeriesData();
    }

    /**
     * Refreshes the generalisation of data
     *
     * @param dataValuesType
     * @param tsId
     */
    @action
    refreshDataAggregation(dataValuesType: DataValueType, tsId: string) {
        // actualize data aggregations, because aggregations are only run on current selected data value type
        // (aggregation is time consuming)
        const data = this.timeSeriesData.get(tsId);
        if (!data) {
            return;
        }
        const tsData = this.generaliseTimeSeriesData(data, tsId, data.length);
        this.addTimeSeriesData(tsId, tsData);
    }

    /**
     * Identify Stations that have multiple object types
     * Remember other object types
     * Has to be done, because result from kiwis does not deliver distinct stations (aggregation is possible on KIWS,
     * but does not deliver all the needed attributes then)
     * Stations are delivered multiple times when they have more than one object type, once for each object type
     * While creating the stations, all entries of a station with the same object type are already reduced/aggregated.
     *
     * @param stations
     */
    async identifyMultiObjectTypeStations(stations: Station[]) {
        // find duplicates (stations with the same stationId)
        const sorted = stations.sort((a, b) => a.id - b.id);

        const multiObjectTypeStations: {[key: string]: any} = {};
        sorted.forEach((station, index) => {
            if (index > 0 && station.id === sorted[index - 1].id) {
                // remember both
                if (!multiObjectTypeStations[station.id]) {
                    multiObjectTypeStations[station.id] = [];
                }
                multiObjectTypeStations[station.id].push(sorted[index - 1]);
                multiObjectTypeStations[station.id].push(station);
            }
        });

        // eslint-disable-next-line guard-for-in
        for (const stationId in multiObjectTypeStations) {
            const pair = multiObjectTypeStations[stationId];
            const first = pair[0];
            const second = pair[1];
            if (!first || !second) {
                return;
            }

            // store reference on each other
            first.relatedObjectType = second.objectType;
            first.relatedStations.set(second.oid, second);
            second.relatedObjectType = first.objectType;
            second.relatedStations.set(first.oid, first);
        }
    }

    /**
     * Change status
     */
    log(message: string, type: StatusType) {
        this.setStatus(message, type);
    }

    /**
     * Check if time series with specified parameterType is configured to have relative data
     * Not all configured time series have relative data present. Method dataValueIndex, returns an index for
     * getting the data, if this method returns -1 when dataValuesType === DataValueType.Relative, no relative data
     * is available
     *
     * @param parameterType
     */
    hasRelativeValues(parameterType: string) {
        return this.config.timeSeries.relativeValuesAvailable.includes(parameterType);
    }

    /**
     * Return the index to use to get data value from time series data array
     * Depends on dataValueType
     *
     * @return the index to get the data from time series data index
     * @returns -1 if no data is available for specified dataValueType
     */
    dataValueIndex(timeSeries: TimeSeries): 1 | 2 | -1 {
        if (this.dataValuesType === DataValueType.Relative) {
            if (timeSeries.dataRelativeValueIndex) {
                return timeSeries.dataRelativeValueIndex === 1 ? 1 : 2;
            } else {
                // no relative data
                return -1;
            }
        } else {
            if (timeSeries.dataAbsoluteValueIndex) {
                return timeSeries.dataAbsoluteValueIndex === 1 ? 1 : 2;
            } else {
                // no absolute data
                return -1;
            }
        }
    }

    /**
     * Load available time series for the specified stations and group
     *
     * @param stations: Stations to load the time series
     */
    @action
    private loadAllTimeSeriesLists(stations: Station[]) {
        this.log(">> Verfügbare Messparameter aller Stationen laden...", StatusType.Loading);

        const loadingTimer = setTimeout(() => {
            // report when kiwis is slow loading stations
            this.log("KIWIS reagiert langsam...", StatusType.Slowly);
        }, 2000);

        const stationIds = stations.map((s) => s.id);

        // Request time series by group to get relation to topics (e.g. Boden, Qualität, Hydrometrie)
        const groupQueries: any[] = [];
        this.config.timeSeries.groups.forEach((group: GroupConfiguration) => {
            groupQueries.push(this.timeSeriesListRequest(stationIds, group));
        });

        // Wait for all group queries to complete to compose one time series list
        Promise.all(groupQueries).then((results: any[]) => {
            if (!results || !results.length) {
                this.log("Fehler beim Laden der Messparameter", StatusType.Error);
                return;
            }

            clearTimeout(loadingTimer);
            this.log(">> Messparameter für alle Themen geladen.", StatusType.Loading);

            // TODO: Warum sind hier nur 43 Bodenzeitreihen in den Results enthalten? Sind Queries korrekt?
            const timeSeriesList: Map<string, TimeSeries> = new Map<string, TimeSeries>();
            results.forEach((result) => {
                result.timeSeriesList.forEach((ts: TimeSeries) => {
                    if (timeSeriesList.has(ts.id)) {
                        // time series already included in another topic (group)
                        // --> merge topics
                        const timeSeries = timeSeriesList.get(ts.id);
                        if (timeSeries) {
                            timeSeries.topics = timeSeries.topics.concat(ts.topics);
                            timeSeriesList.set(ts.id, timeSeries);
                        }
                    } else {
                        timeSeriesList.set(ts.id, ts);
                    }
                });
            });

            runInAction(() => {
                this.replaceTimeSeriesList(timeSeriesList);
                this.log(
                  ">> Messparameter für alle Themen eingelesen.",
                  StatusType.Complete,
                );
                this.updateDataCompleteness(DataCompleteness.TimeSeriesLists);
            });

        }).catch((reason) => {
            // eslint-disable-next-line no-console
            console.error(reason);
        });
    }

    /**
     * Request to load time series by group
     * Return a promise to use together with other request and to be handled by Promise.all
     *
     * @param stationIds The stations to query
     * @param group The group, used to store the results to the group
     */
    private timeSeriesListRequest(stationIds: number[], group: GroupConfiguration) {
        const query = this.getQuery("timeSeriesList") as TimeSeriesListQueryConfiguration;
        if (!query) {
            throw new Error("query for 'timeSeriesList' not defined in 'queries'.");
        }

        // add or remove 'coverage' parameter based on group configuration
        query.returnfields = this.toggleReturnField(
            query.returnfields,
            "coverage",
            group.includeTimeCoverage
        );

        return new Promise((resolve, reject) => {
            query.station_id = stationIds.join(",");
            // filter by group to store time series by topic
            query.timeseriesgroup_id = group.id;

            // enable post request. get request with lots of time series IDs fails
            query.kvp = true;

            return esriRequest(this.config.url, {
                responseType: "json",
                method: "post",
                query
            }).then((result) => {
                const timeSeriesList: TimeSeries[] = [];
                result.data.forEach((item: any) => {
                    // create fresh time series
                    const timeSeries = this.createTimeSeries(item, group);
                    timeSeriesList.push(timeSeries);
                });

                resolve({
                    timeSeriesList,
                    group
                });
            }).catch((error) => {
                reject(error);
            });
        });
    }

    /**
     * Add/remove a field in comma-separated return fields while keeping field names unique.
     */
    private toggleReturnField(returnFields: string | undefined, field: string, include: boolean): string {
        const cleanedField = field.trim();
        const fields = (returnFields || "")
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item.length > 0 && item !== cleanedField);

        if (include) {
            fields.push(cleanedField);
        }

        return fields.join(",");
    }

    /**
     * Stores item information in a new time series object.
     * Updates an existing time series, if present
     *
     * @param item: Result object from KIWIS request
     */
    @action
    private createOrUpdateTimeSeries(item: any) {
        const tsId = item[this.config.timeSeries.fields.id];
        if (this.timeSeriesList.has(tsId)) {
            // update existing time series
            const ts: TimeSeries | undefined = this.timeSeriesList.get(tsId);
            if (!ts) {
                return;
            }
            this.config.timeSeries.fieldMappings.forEach((fieldMap: string[]) => {
                const value = item[fieldMap[1]];
                if (value) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    ts[fieldMap[0]] = value;
                }
            });
        } else {
            // create fresh time series
            const timeSeries = this.createTimeSeries(item);
            this.timeSeriesList.set(timeSeries.id, timeSeries);
        }
    }

    /**
     * Create fresh timeSeries
     */
    @action
    private createTimeSeries(item: any, group?: GroupConfiguration): TimeSeries {
        const {
            fields,
            fieldMappings,
            overrides,
            defaultPeriod
        } = this.config.timeSeries;

        // try to find values for fields specified in mappings
        // store found values in station object
        const props = {};
        fieldMappings.forEach((fieldMap: string[]) => {
            const value = item[fieldMap[1]];
            if (value) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                props[fieldMap[0]] = value;
            } else {
                // if no value is present in item --> initialize value
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                props[fieldMap[0]] = null;
            }
        });
        // remember the data value coverage, because 'from' and 'to' is used to store the selected period and
        // is therefore overridden
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        props["coverage"] = {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            from: props[fields.from],
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            to: props[fields.to]
        };
        // clear initial from/to on reload
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        props[fields.from] = "";
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        props[fields.to] = "";

        if (group) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            props["topics"] = [group.topic];
        }

        const ts = new TimeSeries(props);
        ts.applyOverrides(overrides);

        // set default time period
        ts.periodISO = defaultPeriod[ts.stationParameterName];

        return ts;
    }

    /**
     * Load last measurement values for specified time series
     *
     * @param timeSeriesIds: IDs of time series
     */
    @action
    private loadCurrentMeasurements(timeSeriesIds: string[]) {
        this.log(">>> Aktuelle Messwerte laden...", StatusType.Loading);

        const query = this.getQuery("currentMeasurements") as TimeSeriesValuesQueryConfiguration;
        if (!query) {
            throw new Error("query for 'currentMeasurements' not defined in 'queries'.");
        }
        query.ts_id = timeSeriesIds.join(",");

        const loadingTimer = setTimeout(() => {
            // report when kiwis is slow loading stations
            this.log("KIWIS reagiert langsam...", StatusType.Slowly);
        }, 2000);

        // enable post request. get request with lots of time series IDs fails
        query.kvp = true;

        // eslint-disable-next-line no-console
        console.time("KIWIS: Load current measurements");
        esriRequest(this.config.url, {
            responseType: "json",
            method: "post",
            query
        }).then((result) => {
            // eslint-disable-next-line no-console
            console.timeEnd("KIWIS: Load current measurements");
            clearTimeout(loadingTimer);
            this.log(">>> Messwerte erhalten.", StatusType.Loading);

            // store measurements to the fields on station
            // get station
            // clear measurements
            // store values
            result.data.forEach((value: any) => {
                this.extractAndStoreMeasurement(value);
            });

            this.log(">>> Messwerte geladen.", StatusType.Complete);
            this.updateDataCompleteness(DataCompleteness.CurrentMeasurements);

            this.calculateMeasurementCode();

        }, (error) => {
            clearTimeout(loadingTimer);
            const msg = `Fehler beim Laden der aktuellen Messwerte: ${error}`;
            this.log("Fehler beim Laden der aktuellen Messwerte.", StatusType.Error);
            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
        });
    }

    /**
     * Store time series value to stations
     * A station could exist multiple times on different object_types. The time series values have to be
     * stored on every station
     *
     * @param timeSeriesValue: The value to store
     */
    @action
    private extractAndStoreMeasurement(timeSeriesValue: any) {
        if (timeSeriesValue.data.length === 0 || timeSeriesValue.data[0].length === 0) {
            // no measurement data
            return;
        }

        const {parameterTypeObjectTypeIdMappings, stationParameterMeasurementFieldMappings} = this.config.stations;
        const {fields} = this.config.timeSeries;

        // parameter types are related to 1-n object types
        // a mapping is specified in configuration
        // get base IDs of object types based on parameter type and mapping table
        const parameterType = timeSeriesValue[fields.parameterType];
        const baseIds = parameterTypeObjectTypeIdMappings[parameterType];
        if (!baseIds || baseIds.length === 0) {
            // eslint-disable-next-line no-console
            console.warn(`ParameterType <-> ObjectType mapping not specified in configuration
                                  for: ${parameterType}`);
            return;
        }

        const measurement = this.extractMeasurement(timeSeriesValue);
        const key = stationParameterMeasurementFieldMappings[measurement.stationParameterName];

        // store measurements in station on every object type
        const stationId = timeSeriesValue["station_id"];
        baseIds.forEach((baseId: number) => {
            const oid = Number(baseId + stationId);
            if (!this.stations.has(oid)) {
                return;
            }

            const station = this.stations.get(oid);
            if (!station) {
                return;
            }

            // store measurements, override when values for this parameter type are already stored
            station.currentMeasurements.set(key, measurement);
        });
    }

    //  TODO: Abgeleitete Klassen für Stationen erstellen (z.B. Bodenstation)
    //  und die Berechnung des measurement codes in diese Klassen auslagern
    /**
     * Calculate a code based on measurement values.
     * (Code is used for symbolizing features on map)
     */
    @action
    private calculateMeasurementCode() {
        if (!this.currentMeasurementsLoaded) {
            // eslint-disable-next-line no-console
            console.error("Aktuelle Messwerte nicht verfügbar für Berechnung Klassierungswerte.");
        }

        this.log(">>> Klassierungswert für Darstellung in Karte berechnen...", StatusType.Loading);

        this.stations.forEach((station: Station) => {
            station.calculateMeasurementCode(this.config);
        });

        this.log("Darstellungsklassierung abgeschlossen.", StatusType.Complete);
        this.updateDataCompleteness(DataCompleteness.MeasurementCode);
    }

    /**
     * Get query from config by ID
     *
     * @param id: The ID of the query
     */
    private getQuery(id: string) {
        if (!this.config) {
            throw new Error("Config not found.");
        }
        if (!this.config.queries || this.config.queries.length < 1) {
            throw new Error("No queries defined in config.");
        }

        const query = this.config.queries.filter((item) => {
            return item.id === id;
        })[0];
        return _.clone(query); // return a copy of the query, so it can be changed independently
    }

    /**
     * Parse station list result and extract stations
     *
     * @param result
     */
    private extractStations(result: any) {
        const {
            objectTypes,
            fields,
            fieldMappings,
            overrides,
            fieldAggregations
        } = this.config.stations;

        // eslint-disable-next-line no-console
        console.time("Stations: Process result");
        const stations: Map<number, Station> = new Map<number, Station>();
        result.data.forEach((item: any) => {
            if (this.isIgnorableObjectType(item[fields.objectType])) {
                return null;
            }

            // check coordinates
            const x = Kiwis.extractCoordinate(item[fields.x], 2000000);
            const y = Kiwis.extractCoordinate(item[fields.y], 1000000);
            if (x === -1 || y === -1) {
                return null;
            }

            // try to find values for fields specified in mappings
            // store found values in station object
            const props: StationProps = {
                x: -1,
                y: -1,
                baseId: -1,
                oid: -1,
                description: "",
                topic: ""
            };
            fieldMappings.forEach((fieldMap: string[]) => {
                const value = item[fieldMap[1]];
                if (value) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    props[fieldMap[0]] = value;
                }
            });
            props.x = x;
            props.y = y;

            // create unique id. station id cannot be used: station id is the same even when the station
            // belongs to multiple object_types
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const baseId = objectTypes.baseIds[props["objectType"]];
            props.baseId = baseId;
            props.oid = Number(baseId + props["id"]);

            // get topic for this station
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            props.topic = objectTypes.topics[baseId];

            props["online"] = props["online"] === "yes" ? "yes" : "no";

            if (!stations.has(props.oid)) {
                runInAction(() => {
                    const station = new Station(props);
                    station.store = this; // Stations should know its store
                    station.applyOverrides(overrides);
                    stations.set(station.oid, station);
                });
            } else {
                // KIWIS is returning flat JSON only. If parameter type is requested, stations are
                // returned multiple times, once for each parameter type
                // --> merge objects to keep unique stations
                const station = stations.get(props.oid);
                if (station) {
                    runInAction(() => {
                        station.aggregate(props, fieldAggregations);
                    });
                }
            }
        });
        // eslint-disable-next-line no-console
        console.timeEnd("Stations: Process result");
        // eslint-disable-next-line no-console
        console.info(`Stations: ${stations.size} Stationen geladen.`);
        return stations;
    }

    /**
     * Parse result from document server
     *
     * @param stations
     */
    private parseDocumentsResult(stations: StationDocuments[]): Map<string, StationDocuments> {
        const stationDocuments: Map<string, StationDocuments> = new Map<string, StationDocuments>();

        runInAction(() => {
            stations
                .filter((stationData: StationDocuments) => stationData.id && stationData.id.length)
                .forEach((stationData: StationDocuments) => {
                    const documents = new StationDocuments(stationData);
                    documents.store = this; // documents should know its store
                    stationDocuments.set(documents.id, documents);
                });
        });

        return stationDocuments;
    }

    /**
     * Extract measurement data from KIWIS time series data query result
     *
     * @param item
     */
    private extractMeasurement(item: any) {
        const {value, relativeValue} = this.extractValues(item);
        const {fields, overrides} = this.config.timeSeries;

        const measurement = new Measurement({
            date: new Date(item.data[0][0]),
            value,
            relativeValue,
            unit: item[fields.unitSymbol],
            parameterType: item[fields.parameterType],
            stationParameterName: item[fields.stationParameter],
            tsId: item[fields.id],
            name: item[fields.name]
        });

        // apply overrides
        measurement.applyOverrides(overrides);

        return measurement;
    }

    // noinspection JSMethodCanBeStatic
    /**
     * Extract relative and absolute values from KIWIS result
     * Only return a relative value and relative index when a second data value is present
     *
     * @param item
     */
    private extractValues(item: any) {
        // Identify absolute value by comparing values, bigger value is absolute value
        const firstValue = (item.data[0][1]) * 1; // convert to number

        const hasSecondValue = typeof item.data[0][2] !== "undefined" && item.data[0][2] != null;
        if (hasSecondValue) {
            const secondValue = (item.data[0][2]) * 1;
            if (secondValue > firstValue) {
                // second value is absolute value
                return {
                    value: secondValue,
                    absoluteValueIndex: 2,
                    relativeValue: firstValue,
                    relativeValueIndex: 1
                };
            } else {
                // first value is absolute value
                return {
                    value: firstValue,
                    absoluteValueIndex: 1,
                    relativeValue: secondValue,
                    relativeValueIndex: 2
                };
            }
        }

        return {
            value: firstValue,
            absoluteValueIndex: 1,
            relativeValue: null,
            relativeValueIndex: null
        };
    }

    /**
     * Check, if object type has to be ignored
     * e.G. 'Allgemein' is a hierarchical specialism and has to be ignored
     *
     * @param objectType
     */
    private isIgnorableObjectType(objectType: string) {
        return this.config.stations.objectTypes.ignore.indexOf(objectType) !== -1;
    }

    /**
     * Generalize data, filter to dense data based on period
     * Use configured interval to reduce data
     *
     * @param data
     * @param timeSeriesId
     * @param rows
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private generaliseTimeSeriesData(data: TimeSeriesDataTuple[] | null, timeSeriesId: string, rows: number):
    TimeSeriesDataTuple[] | null {
        this.updateGeneralisationStatus(GeneralisationStatus.Calculating);
        if (!data) {
            this.updateGeneralisationStatus(GeneralisationStatus.Complete);
            return null;
        }

        const timeSeries = this.timeSeriesList.get(timeSeriesId);
        if (!timeSeries || (!timeSeries.periodISO && !timeSeries.DEFAULT_PERIOD)) {
            this.updateGeneralisationStatus(GeneralisationStatus.Complete);
            return data;
        }

        const generalisationConfig =this.config.getGeneralisationConfig(timeSeries);
        if (!generalisationConfig) {
            this.updateGeneralisationStatus(GeneralisationStatus.Error);
            throw new Error(`No generalization defined for ${timeSeries.parameterTypeName}`);
        }
        const generalisationRule = this.config.getGeneralisationRule(timeSeries);
        if (!generalisationRule || !generalisationRule.reducer) {
            // no rule or no interval defined
            this.updateGeneralisationStatus(GeneralisationStatus.Complete);
            return data;
        }

        const valueIndex = this.dataValueIndex(timeSeries);
        if (valueIndex === -1) {
            const msg = `No data index found for the specified dataValueType for: 
                ${timeSeries.parameterTypeName} - ${timeSeries.name}`;

            // eslint-disable-next-line no-console
            console.error(msg);
            throw new Error(msg);
        }
        const gData = this.generaliseValues(data, generalisationRule, generalisationConfig.aggregationType, valueIndex);
        this.updateGeneralisationStatus(GeneralisationStatus.Complete);

        return gData;
    }

    /**
     * Aggregate data by using D3.js rollup.
     * https://observablehq.com/@d3/d3v6-migration-guide#group
     * https://github.com/d3/d3-array/blob/main/README.md#rollup
     *
     * Rollup creates n-dimensional trees out of data.
     * Rollup function is used to apply summary functions to each group of leaf elements, to build sum or mean values.
     * Date formatter is used to define the level of nesting. By overriding (fixing) a part of the datetime, values are
     * grouped when using datetime as filter.
     *
     * @example: Per Hour: Set fixed minutes and seconds to nest per hour [YYYY-MM-DDTHH:00:00]
     * @example: Per Day: Set fixed hours to nest per day [YYYY-MM-DDT12:00:00]
     * @example: Per Month: Set fixed day to nest per month [YYYY-MM-15T12:00:00]
     * @param data: The data array
     * @param rule: The generalisation rule
     * @param aggregationType: Type of aggregation
     * @param valueIndex: The index to access the value
     */
    private generaliseValues(data: TimeSeriesDataTuple[], rule: GeneralisationRuleConfiguration,
                             aggregationType: DataAggregationType, valueIndex: 1 | 2): TimeSeriesDataTuple[] | null {

        if (!rule.reducer) {
            return data;
        }

        if (rule.preselect) {
            data = this.generalisationPreselection(data, rule.preselect);
        }

        // aggregate values to an d3.InternMap https://github.com/mbostock/internmap
        const aggregateMap = d3.rollup(data,
            (v: any) => {
                // values in a group are reduced by this
                if (aggregationType === DataAggregationType.Mean) {
                    return d3.mean(v, (d: TimeSeriesDataTuple) => d[valueIndex]);
                } else if (aggregationType === DataAggregationType.Sum) {
                    // values in a group are reduced by this
                    return d3.sum(v, (d: TimeSeriesDataTuple) => d[valueIndex]);
                }
            },
            (d: TimeSeriesDataTuple) => {
                // entries in the map are identified (grouped) by this key.
                // use date formatter to group all desired values together
                return dayjs(Date.parse(d[0])).format(rule.reducer);
            }
        );

        if (!aggregateMap) {
            // eslint-disable-next-line no-console
            console.error("Error aggregating data.");
            return data;
        }

        // workaround: add value twice to get a data tuple with 3 entries. This is needed because the data value index
        // is calculated based on original data array with 3 entries [date, value1, value2]
        return Array.from(aggregateMap, (entry: any) => {
            return [entry[0], entry[1], entry[1]];
        });
    }

    /**
     * Preselect data by grouping and rollup
     *
     * @param data
     * @param preselect
     */
    private generalisationPreselection(data: TimeSeriesDataTuple[], preselect: GeneralisationRuleConfiguration) {
        // group by date. use formatter to define the level of nesting.
        const selectionMap = d3.rollup(data,
            (v: any) => {
                // values in a group are reduced by this
                if (preselect.pluck === PluckIndex.First) {
                    return v[0];
                }
                if (preselect.pluck === PluckIndex.Last) {
                    return v[v.length - 1];
                }
            },
            (d: TimeSeriesDataTuple) => {
                // entries in the map are identified (grouped) by this key.
                // use date formatter to group all desired values together
                return dayjs(Date.parse(d[0])).format(preselect.reducer);
            }
        );

        return Array.from(selectionMap, (entry: any) => {
            return entry[1];
        });
    }


    /**
     * Extend date range based on configured rules
     *
     * It's important to extend the time range when requesting data that is aggregated.
     * e.g. precipitation sums of the last month should include all values of the whole month, not only 30 days back
     * from the current date.
     * When requesting kiwis with P1M, it's returning the last 30 days from current date, not the whole last month
     *
     * To extend the time, the current datetime is formatted and used to calculate 'from' and 'to' date depending on
     * the selected period.
     *
     * @example: Extend to days: Set fix hours to extend to days.
     *           from: [YYYY-MM-DDT00:00:00], to: [YYYY-MM-DDT23:59:59]
     * @example: Extend to months: Set fixed days to extend to months
     *           from: [YYYY-MM-01T00:00:00], to: [YYYY-MM-31T23:59:59]
     * @example: Extend to years: Set fixed months to extend to months
     *           from: [YYYY-01-01T00:00:00], to: [YYYY-12-31T23:59:59]
     */
    private extendDateRange(timeSeries: TimeSeries | null, dateRange: DateRangeQueryParams): DateRangeQueryParams {
        if (!timeSeries || !dateRange.period) {
            return dateRange;
        }

        const today: Date = new Date();
        const fromDate: Date = new Date();

        if (dateRange.period.includes("H")) {
            const hours = dateRange.extractPeriodNumber;
            fromDate.setHours(fromDate.getHours() - hours);
        }
        if (dateRange.period.includes("D")) {
            const days = dateRange.extractPeriodNumber;
            fromDate.setDate(fromDate.getDate() - days);
        }
        else if (dateRange.period.includes("M")) {
            const months = dateRange.extractPeriodNumber;
            fromDate.setMonth(fromDate.getMonth() - months);
        }
        else if (dateRange.period.includes("Y")) {
            const years = dateRange.extractPeriodNumber;
            fromDate.setFullYear(fromDate.getFullYear() - years);
        }

        const extensionRule = this.config.getTimeRangeExtensionRule(timeSeries, dateRange.period);
        if (!extensionRule || (!extensionRule.extendFrom && !extensionRule.extendTo)) {
            // no rule or no interval defined
            // remember the requested 'from' and 'to' dates on time series
            // when aggregation is done on data, the originally requested time extent is lost
            timeSeries.dataRequestedFromDate = fromDate;
            timeSeries.dataRequestedToDate = today;
            return dateRange;
        }

        // apply rule
        const extendedFrom = dayjs(fromDate).format(extensionRule.extendFrom);
        const extendedTo = dayjs(today).format(extensionRule.extendTo);

        // remember the requested 'from' and 'to' dates on time series
        // when aggregation is done on data, the originally requested time extent is lost
        // use the date after rule was applied. convert to date again
        timeSeries.dataRequestedFromDate = dayjs(extendedFrom, "YYYY-MM-DDTHH:mm:ss").toDate();
        timeSeries.dataRequestedToDate = dayjs(extendedTo, "YYYY-MM-DDTHH:mm:ss").toDate();

        return new DateRangeQueryParams({
            from: extendedFrom,
            to: extendedTo
        });
    }

    /**
     * Get threshold values for selected thresholds from station object
     * Calc min/max values
     * Store threshold values for selected thresholds per time series
     *
     * @param key
     * @param thresholds
     */
    private storeThresholdValues(key: string, thresholds: Array<string | number | boolean> | null) {
        if (!key || !thresholds) {
            return;
        }
        const ts = this.timeSeriesList.get(key);
        if (!ts) {
            return;
        }

        const station = this.getStationByTimeSeries(ts);
        if (!station) {
            return;
        }

        const values: Array<number | null> = thresholds.map((t) => {
            const tValue = station[`${t}`];
            if (!tValue) {
                // this threshold is not defined for this station
                return null;
            }
            if (!isNaN(tValue)) {
                return tValue;
            }
            const vals = tValue.match(/[-+]?\d+\.?\d+/);
            if (!vals || !vals[0]) {
                return null;
            }
            return parseFloat(vals[0]);
        });
        const verified: number[] = values.filter((v) => v != null) as number[];

        // store values of selected thresholds
        this.selectedThresholdValues.set(key, verified);

        // store min/max values of selected thresholds
        this.selectedMinThresholdValue.set(key, Math.min(...verified));
        this.selectedMaxThresholdValue.set(key, Math.max(...verified));
    }
}
